import React, { type ReactNode } from 'react'
import { createContext, useCallback, useContext, useState } from 'react'

import { notification as antNotification } from 'antd'
import classNames from 'classnames'
import { nanoid } from 'nanoid'

import closeIcon from 'assets/icons/close-notification.svg'
import errorIcon from 'assets/icons/info-white.svg'

import { ALERT_CONSTANTS } from 'common/constants/alertConstants'
import { ALT_CONSTANTS } from 'common/constants/altConstants'
import type { IAlert } from 'common/interfaces/IAlert'
import { UtilService } from 'common/services/utilService'

interface IProps {
  notification: React.ReactElement
  setNotification: (data: IAlert) => void
  setNotificationScope: (scope: NotificationScope) => void
}

type NotificationScope = 'default' | 'charts'

const CHARTS_NOTIFICATION_KEY = 'charts-notification'
const CHARTS_NOTIFICATION_BOTTOM_PX = 72
const DEFAULT_NOTIFICATION_DURATION_SECONDS = 7

const NotificationContext = createContext<IProps | null>(null)

// A list of lines shows one per row; the charts toast keeps a single line.
const descriptionOf = (description: IAlert['description'], inline: boolean): ReactNode => {
  if (!Array.isArray(description)) return description
  if (inline) return description.join(' ')
  return description.map((line, index) => <div key={index}>{line}</div>)
}

export const NotificationProvider = ({ children }: { children: ReactNode }) => {
  const [defaultApi, defaultNotification] = antNotification.useNotification()
  const [chartsApi, chartsNotification] = antNotification.useNotification({
    bottom: CHARTS_NOTIFICATION_BOTTOM_PX,
  })
  const [notificationScope, setNotificationScope] = useState<NotificationScope>('default')

  const setNotification = useCallback(
    ({ title, description, type, duration }: IAlert) => {
      const isChartsScope = notificationScope === 'charts'
      const api = isChartsScope ? chartsApi : defaultApi
      const key: string = isChartsScope ? CHARTS_NOTIFICATION_KEY : nanoid()

      api.success({
        key,
        message: !isChartsScope && type !== ALERT_CONSTANTS.SUCCESS ? title : undefined,
        description: descriptionOf(description, isChartsScope),
        placement: isChartsScope ? 'bottomRight' : 'bottomLeft',
        duration: duration ?? DEFAULT_NOTIFICATION_DURATION_SECONDS,
        pauseOnHover: isChartsScope ? false : undefined,
        className: classNames(
          'notification',
          `notification${UtilService.capitalizeFirstLetter(type)}`,
          {
            notificationCharts: isChartsScope,
          },
        ),
        icon:
          type !== ALERT_CONSTANTS.SUCCESS ? (
            <img alt={ALT_CONSTANTS.INFO_ICON} src={errorIcon} />
          ) : (
            <></>
          ),
        closeIcon: isChartsScope ? null : type !== ALERT_CONSTANTS.SUCCESS ? (
          <img alt={ALT_CONSTANTS.ACTION_ICON} src={closeIcon} />
        ) : (
          <></>
        ),
      })
    },
    [chartsApi, defaultApi, notificationScope],
  )

  const notification = (
    <>
      {defaultNotification}
      {chartsNotification}
    </>
  )

  return (
    <NotificationContext.Provider value={{ setNotification, notification, setNotificationScope }}>
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotification = () => {
  return useContext(NotificationContext) as IProps
}
